import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'chart.js/auto';

// Define procurement risk metrics with keys, display names, values, and detailed hypothetical data.
const metrics = [
  {
    key: "vendorVetting",
    name: "Vendor Fraud Alerts",
    value: "12",
    details: "Vendor Fraud Alerts: In the last quarter, 12 fraud alerts were triggered during vendor vetting, indicating discrepancies in vendor profiles. Enhanced due diligence and periodic background checks are recommended. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  },
  {
    key: "invoiceMatching",
    name: "Invoice Discrepancies",
    value: "8",
    details: "Invoice Discrepancies: There have been 8 instances of mismatched invoices in the past quarter, suggesting potential manual errors or fraudulent modifications. Implementing automated invoice reconciliation can mitigate this risk. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  },
  {
    key: "poApproval",
    name: "Delayed PO Approvals",
    value: "5 days",
    details: "Delayed PO Approvals: An average delay of 5 days in purchase order approvals could signal bottlenecks and increased fraud risk. Streamlining the approval process is advised to improve efficiency and reduce risk. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  },
  {
    key: "contractCompliance",
    name: "Contract Non-Compliance Cases",
    value: "3",
    details: "Contract Non-Compliance Cases: With 3 cases recorded, lapses in contract compliance have been identified, which could expose the organization to financial and legal risks. Regular audits and tighter control measures are needed. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  },
  {
    key: "highRiskTransactions",
    name: "High-Risk Transactions",
    value: "7%",
    details: "High-Risk Transactions: 7% of procurement activities are flagged as high-risk, requiring enhanced monitoring and proactive risk management. Continuous improvements in internal controls are recommended. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  }
];

// Helper function to generate dummy instances for a given metric.
const generateInstances = (key, count, metricName) => {
  const prefixes = {
    vendorVetting: "VND",
    invoiceMatching: "INV",
    poApproval: "PO",
    contractCompliance: "CTR",
    highRiskTransactions: "HRT"
  };
  const prefix = prefixes[key] || "GEN";
  const statuses = ["Investigating", "Resolved", "Pending", "Under Review"];
  const instances = [];
  for (let i = 1; i <= count; i++) {
    instances.push({
      id: `${prefix}${String(i).padStart(3, '0')}`,
      control: metricName,
      description: `Instance ${i} for ${metricName}.`,
      date: "2025-03-10",
      status: statuses[i % statuses.length]
    });
  }
  return instances;
};

const getInstancesForControl = (selectedControl) => {
  let instances = [];
  if (selectedControl === "all") {
    metrics.forEach(metric => {
      // Extract numeric count (ignoring non-numeric characters).
      const count = parseFloat(metric.value) || 0;
      const generated = generateInstances(metric.key, Math.floor(count), metric.name);
      instances = instances.concat(generated);
    });
  } else {
    const metric = metrics.find(m => m.key === selectedControl);
    const count = parseFloat(metric.value) || 0;
    instances = generateInstances(metric.key, Math.floor(count), metric.name);
  }
  return instances;
};

const MiniCards = ({ selectedControl, onCardClick }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
      {metrics.map((metric) => (
        <div 
          key={metric.key}
          onClick={() => onCardClick(metric.key)}
          className={`p-4 bg-white shadow rounded cursor-pointer hover:bg-blue-50 transition ${
            selectedControl === metric.key ? "border-2 border-blue-600" : ""
          }`}
        >
          <h3 className="text-lg font-semibold text-gray-700">{metric.name}</h3>
          <p className="text-2xl font-bold text-blue-600">{metric.value}</p>
        </div>
      ))}
    </div>
  );
};

const ProcurementProcessPage = () => {
  const chartRef = useRef(null);
  const [selectedControl, setSelectedControl] = useState("all");
  const [fraudChart, setFraudChart] = useState(null);

  // Get current date for "Last Updated" display.
  const currentDate = new Date().toLocaleDateString();

  useEffect(() => {
    // Destroy an existing chart instance before creating a new one.
    if (fraudChart) {
      fraudChart.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    // Define labels and data based on the selected control filter.
    let labels = [];
    let data = [];
    if (selectedControl === "all") {
      labels = metrics.map(metric => metric.name);
      data = metrics.map(metric => {
        const numericValue = parseFloat(metric.value);
        return isNaN(numericValue) ? 0 : numericValue;
      });
    } else {
      const metric = metrics.find(m => m.key === selectedControl);
      labels = [metric.name];
      const numericValue = parseFloat(metric.value);
      data = [isNaN(numericValue) ? 0 : numericValue];
    }

    const newChart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [{
          label: "Fraud Risk Score",
          data: data,
          backgroundColor: [
            "rgba(255, 99, 132, 0.6)",
            "rgba(54, 162, 235, 0.6)",
            "rgba(255, 206, 86, 0.6)",
            "rgba(75, 192, 192, 0.6)",
            "rgba(153, 102, 255, 0.6)"
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)"
          ],
          borderWidth: 1
        }]
      },
      options: {
        scales: {
          y: { beginAtZero: true }
        },
        plugins: {
          legend: {
            labels: {
              font: { size: 14 }
            }
          }
        }
      }
    });

    setFraudChart(newChart);

    // Cleanup: destroy chart instance on component unmount.
    return () => {
      newChart.destroy();
    };
  }, [selectedControl]);

  const handleCardClick = (controlKey) => {
    setSelectedControl(controlKey);
  };

  // Render details based on the selected control.
  const getDetails = () => {
    if (selectedControl === "all") {
      return "Overall Procurement Risk Analysis: Aggregated data across all critical controls indicates areas requiring further scrutiny. Click on any card for detailed insights. Disclaimer: The data provided is hypothetical and for demonstration purposes only.";
    }
    const metric = metrics.find(m => m.key === selectedControl);
    return metric ? metric.details : "No details available. Disclaimer: The data provided is hypothetical and for demonstration purposes only.";
  };

  // Get filtered/generated instances based on the selected control.
  const instances = getInstancesForControl(selectedControl);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col px-4 py-8">
      <header className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-blue-700">
            Procurement Department - High Risk & Fraud Controls
          </h1>
          <p className="text-gray-600">Last Updated: {currentDate}</p>
        </div>
      </header>
      <nav className="mb-4">
        <Link 
          to="/departments" 
          className="text-blue-500 underline hover:text-blue-600"
        >
          Back to Departments
        </Link>
      </nav>
      {/* Clickable Mini Cards Dashboard */}
      <MiniCards selectedControl={selectedControl} onCardClick={handleCardClick} />
      <section className="flex flex-col items-center">
        {/* Alternative Filter Component */}
        <div className="mb-6">
          <label htmlFor="controlFilter" className="mr-2 font-medium">
            Filter Control:
          </label>
          <select 
            id="controlFilter" 
            value={selectedControl} 
            onChange={(e) => setSelectedControl(e.target.value)} 
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            {metrics.map(metric => (
              <option key={metric.key} value={metric.key}>{metric.name}</option>
            ))}
          </select>
        </div>
        {/* Chart Container */}
        <div className="w-full max-w-2xl bg-white p-6 rounded shadow">
          <canvas ref={chartRef} width="400" height="200"></canvas>
        </div>
        {/* Details Section */}
        <div className="w-full max-w-2xl bg-white p-6 rounded shadow mt-6">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Details</h2>
          <p className="text-gray-600">{getDetails()}</p>
        </div>
        {/* Instances Table Section */}
        <div className="w-full max-w-2xl bg-white p-6 rounded shadow mt-6 overflow-x-auto">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Instances Identified</h2>
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Instance ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Control</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {instances.map(instance => (
                <tr key={instance.id} className="border-t">
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.id}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.control}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.description}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.date}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ProcurementProcessPage;
